import { useEffect, useRef, useState } from "react";
import type { PageProps } from "../pages/Page";
import type { TealiumScriptProps, ScriptMetaData } from "./types";

declare global {
  interface Window {
    newrelic?: {
      log(message: string): void;
    };
  }
}

export const getTealiumScriptProps = (
  pageData: PageProps,
): TealiumScriptProps | undefined =>
  pageData.scripts?.find(
    ({ metadata }: ScriptMetaData) => metadata.name === "tealium",
  )?.props as TealiumScriptProps;

const shiftPrefix = (name: string): string => {
  return "data-insights-event-" + name;
};

const getAttributeValue = (element: Element, name: string): string => {
  return element.getAttribute(shiftPrefix(name)) || "";
};

const validEmail = (email: string): boolean => {
  return /^.+@.+\..+$/.test(email);
};

type SyntheticEventParams = {
  syntheticEventAction: string;
  syntheticEventCategory: string;
  syntheticEventLabel: string;
};

// Temporary debug logging function that leverages Chopin's NewRelic, if available
const debugLog = (message: string, data?: Record<string, unknown>) => {
  const logMessage = message + (data ? ` - ${JSON.stringify(data)}` : "");

  if (!window || !window.newrelic) {
    console.log(logMessage);
    return;
  }

  window.newrelic.log(logMessage);
};

/*
  This is a recreation of the Tealium snippet in the legacy journey for CTM
  https://github.com/simplybusiness/chopin/blob/master/app/views/shared/tracking/_tealium.html.erb

  NB: fireSyntheticEvent is assumed to be "true" for Q2Q - we will always fire the QuoteStarted event on page load and the QuoteEnded event when the Continue to Quote button is clicked.
*/
const loadTealiumTracking = ({
  appNameField,
  appPlatform,
  appVersion,
  campaignMaskcode,
  customerId,
  customerPartialPostcode,
  enquiryId,
  insurerName,
  pageEventCategory,
  pageEventId,
  pageNameField,
  pageTypeField,
  paymentPreference,
  policyStartDate,
  primaryDetail,
  productDetail,
}: TealiumScriptProps) => {
  if (!window.utag || !window.tracking) {
    //NB: since we guard against calling this function before checking these exist, this should never happen.
    return;
  }

  // Create local references to avoid optional chaining everywhere
  const utag = window.utag;
  const tracking = window.tracking;
  const hashString = tracking.hashString || ((s: string) => s); // Fallback if hashString is not defined
  const questionnaire = document.getElementsByClassName(
    "mobius-questionnaire",
  )[0];

  // Create the first utag update
  const utagData: Record<string, string> = {
    app_name: appNameField,
    app_platform: appPlatform,
    app_version: appVersion,
    brand_name: insurerName || "",
    campaign_maskcode: campaignMaskcode || "",
    customer_id: customerId ? hashString(customerId) : "",
    customer_partialpostcode: customerPartialPostcode || "",
    enquiry_id: enquiryId || "",
    page_name: pageNameField,
    page_type: pageTypeField,
    policy_start_date: policyStartDate || "",
    product_detail: productDetail || "",
    product_paymenttype: paymentPreference || "",
    trade_type: primaryDetail || "",
  };
  utag.data = { ...utag.data, ...utagData };

  //Fire a Utag page view
  utag.view(utag.data);

  // Register events on all elements with data-insights-event-label attribute
  const insightsElements = document.querySelectorAll(
    `[${shiftPrefix("label")}]`,
  );
  insightsElements.forEach(element => {
    const eventType = getAttributeValue(element, "tealium-event");
    if (eventType) {
      element.addEventListener(
        eventType,
        function onceHandler() {
          const eventData = {
            event_category: getAttributeValue(element, "category"),
            event_action: getAttributeValue(element, "action"),
            event_label: getAttributeValue(element, "label"),
          };
          utag.data = { ...utag.data, ...eventData };
          utag.link(utag.data);
          element.removeEventListener(eventType, onceHandler); // Ensure it only runs once
        },
        { once: true },
      );
    }
  });

  // Watch for email input field to be added to the DOM, then do a utag update on blur
  const emailObserver = new MutationObserver((_mutations, observer) => {
    const customerEmailAddress = questionnaire.querySelector(
      "input[id='customer[email_address]']",
    ) as HTMLInputElement;

    if (customerEmailAddress) {
      debugLog("emailAddressFieldFound");
      let lastEmailAddress: string = "";

      customerEmailAddress.addEventListener("blur", function () {
        const emailAddress = customerEmailAddress.value;

        if (validEmail(emailAddress) && emailAddress !== lastEmailAddress) {
          lastEmailAddress = emailAddress;

          const eventData = {
            customer_id: hashString(emailAddress.trim()),
            event_category: pageEventCategory,
            event_action: "sendCustomerId",
            event_label: pageEventId,
            event_value: "",
          };
          utag.data = { ...utag.data, ...eventData };

          debugLog("emailAddressBlurred", eventData);
          utag.link(utag.data);
        }
      });

      observer.disconnect();
    }
  });
  emailObserver.observe(questionnaire, { childList: true });

  // Function to fire a synthetic event for Celebrus tracking
  const fireCelebrusSyntheticAction = (params: SyntheticEventParams) => {
    const actionData: Record<string, string> = {
      event_category: params.syntheticEventCategory,
      event_label: params.syntheticEventLabel,
      event_action: params.syntheticEventAction,
    };

    utag.data = { ...utag.data, ...actionData };

    debugLog("fireCelebrusSyntheticAction", { ...utag.data });
    utag.link(utag.data);
  };

  // The original version of this tracking script fired this synthetic action on page load
  //This appears to be required in order to link event category, label, and action to the view event
  //See: https://github.com/simplybusiness/chopin/blob/e4538efd3399a2dcc765f92608865b46a9d63dfa/app/views/shared/tracking/_tealium.html.erb#L162
  fireCelebrusSyntheticAction({
    syntheticEventAction: "sendQuoteStartedEvent",
    syntheticEventCategory: "QuoteStarted",
    syntheticEventLabel: "QuoteStarted_NewQuote",
  });

  // Start watching the questionnaire for the Continue to Quote button to be added, then add a click listener to it to ensure that we fire the QuoteEnded event when it's clicked.
  const continueToQuoteButtonObserver = new MutationObserver(() => {
    const ctqButton = questionnaire.querySelector(
      ".mobius-submit-action button",
    ) as HTMLInputElement;
    if (ctqButton) {
      debugLog("continueToQuoteButtonFound");
      ctqButton.addEventListener(
        "click",
        () => {
          fireCelebrusSyntheticAction({
            syntheticEventAction: "sendQuoteEndedEvent",
            syntheticEventCategory: "QuoteEnded",
            syntheticEventLabel: "QuoteEnded",
          });
        },
        { once: true },
      );

      continueToQuoteButtonObserver.disconnect();
    }
  });

  continueToQuoteButtonObserver.observe(questionnaire, { childList: true });
};

export const CtmTealiumProvider = (props: TealiumScriptProps) => {
  const [utagScriptLoaded, setUtagScriptLoaded] = useState(false);
  const [tealiumScriptLoaded, setTealiumScriptLoaded] = useState(false);
  const [utagAndTrackingAvailable, setUtagAndTrackingAvailable] =
    useState(false);

  // Set to true once the bootstrap useEffect has run once
  const providerLoaded = useRef<boolean>(false);

  // Set to true once all of the dependencies have loaded and the Tealium utag setup is done / page view has been tracked.
  const providerInitialized = useRef<boolean>(false);

  useEffect(() => {
    if (providerLoaded.current) return; //Don't load scripts more than once

    if (!props.quickToQuote) return; // if we aren't in Q2Q don't load all this

    //Add the UTAG loader script. Once it loads, save some state to check that it loaded.
    const utagScript = document.createElement("script");
    utagScript.src = `https://tags.tiqcdn.com/utag/ctm/${props.snippetUrl}/utag.js`;
    utagScript.type = "text/javascript";
    utagScript.async = true;
    utagScript.onload = () => {
      setUtagScriptLoaded(true);
    };
    document.head.appendChild(utagScript);

    //Add the Tealium hash script. Once it loads, save some state to check that it loaded.
    const tealiumScript = document.createElement("script");
    tealiumScript.src = `https://chronicle.comparethemarket.com/hash.js`;
    tealiumScript.type = "text/javascript";
    tealiumScript.classList = "optanon-category-C0001";
    tealiumScript.async = true;
    tealiumScript.onload = () => {
      setTealiumScriptLoaded(true);
    };

    document.head.appendChild(tealiumScript);

    //Watch the document until the CTM tracking provider's downstream scripts load the window properties this depends on.
    const documentObserver = new MutationObserver((_mutations, observer) => {
      if (window.utag && window.tracking) {
        //All tracking dependencies are loaded.
        setUtagAndTrackingAvailable(true);
        observer.disconnect();
      }
    });

    documentObserver.observe(document, { childList: true, subtree: true });

    return () => {
      document.head.removeChild(tealiumScript);
      document.head.removeChild(utagScript);
      documentObserver.disconnect();
    };
  }, [providerLoaded, props]);

  //UseEffect which handles setting up Tealium once all dependencies are loaded and readied.
  useEffect(() => {
    if (providerInitialized.current) {
      return; //Don't set up utag or track page view more than once
    }

    if (utagScriptLoaded && tealiumScriptLoaded && utagAndTrackingAvailable) {
      providerInitialized.current = true;
      loadTealiumTracking(props);
    }
  }, [
    utagScriptLoaded,
    tealiumScriptLoaded,
    utagAndTrackingAvailable,
    providerInitialized,
    props,
  ]);

  return null;
};
